import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
} from '../styles';
import { Button, Container, Heading, HtmlContent, Video } from './ui';

const StyledMediaContent = styled.section`
  ${sectionMargins()};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    gap: 40px;
  `}

  ${minBreakpointQuery.medium`
    gap: 60px;
  `}

  ${minBreakpointQuery.large`
    gap: 80px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 100px;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  ${maxBreakpointQuery.smedium`
    aspect-ratio: 2/1;
  `}

  ${maxBreakpointQuery.small`
    aspect-ratio: 3/2;
  `}

  ${maxBreakpointQuery.tsmall`
    aspect-ratio: 4/3;
  `}

  ${minBreakpointQuery.smedium`
    height: 100%;
  `}

  ${({ flip }) => {
    if (flip) {
      return css`
        ${minBreakpointQuery.smedium`
          grid-row: 1;
          grid-column: 2;
        `}
      `;
    }
  }}

  ${({ version }) => {
    if (version === 1) {
      return css`
        border-radius: 10px;
      `;
    } else if (version === 2) {
      return css`
        ${({ flip }) => (flip ? 'margin-right: -30px;' : 'margin-left: -30px;')}

        ${maxBreakpointQuery.smedium`
          ${({ flip }) =>
            flip ? 'margin-left: -30px;' : 'margin-right: -30px;'}
        `}
      `;
    }
  }}
`;

const StyledContent = styled.div`
  ${minBreakpointQuery.smedium`
    margin-top: 30px;
    margin-bottom: 30px;
  `}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${minBreakpointQuery.smedium`
          ${({ flip }) => (flip ? 'margin-left: auto;' : 'margin-right: auto;')}
          max-width: 520px;
        `}
      `;
    }
  }}
`;

const StyledText = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}

  ul {
    li {
      padding-left: 26px;

      ${minBreakpointQuery.small`
        padding-left: 30px;
      `}

      ${minBreakpointQuery.large`
        padding-left: 34px;
      `}

      &:before {
        top: 5px;
        left: 5px;
        height: 14px;
        width: 8px;
        background: none;
        border: 3px solid ${brandColours.secondary};
        border-top: none;
        border-left: none;
        border-radius: 2px;
        transform: rotate(45deg);
      }
    }
  }
`;

const StyledButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;

  ${minBreakpointQuery.small`
    gap: 16px;
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    gap: 20px;
    margin-top: 30px;
  `}
`;

const MediaContent = ({
  media,
  videoThumbnailSeconds,
  heading,
  text,
  firstLink,
  secondLink,
  flip,
  version,
}) => (
  <StyledMediaContent>
    <Container wide={version === 2}>
      <StyledInner>
        {media.gatsbyImageData ? (
          <StyledImage
            image={media.gatsbyImageData}
            alt={media.alt}
            flip={flip}
            version={version}
          />
        ) : (
          <Video data={media} thumbnailSeconds={videoThumbnailSeconds} />
        )}
        <StyledContent flip={flip} version={version}>
          <Heading>{heading}</Heading>
          {text && <StyledText html={text} />}
          {(firstLink || secondLink) && (
            <StyledButtons>
              {firstLink && (
                <Button to={firstLink.pageUrl}>{firstLink.text}</Button>
              )}
              {secondLink && (
                <Button to={secondLink.pageUrl}>{secondLink.text}</Button>
              )}
            </StyledButtons>
          )}
        </StyledContent>
      </StyledInner>
    </Container>
  </StyledMediaContent>
);

export default MediaContent;
